import { NavLink, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types';
import { cn } from '@/lib/utils';

interface NavItem {
  label: string;
  to: string;
  icon: string;
}

function getNavItems(role: UserRole | undefined): NavItem[] {
  switch (role) {
    case UserRole.ADMIN:
      return [
        { label: 'Dashboard', to: '/admin/dashboard', icon: '/sidebar/dashboard.svg' },
        {
          label: 'Match Verification',
          to: '/admin/match-verification',
          icon: '/sidebar/match-verification.svg',
        },
        { label: 'Approvals', to: '/admin/approval', icon: '/sidebar/approvals.svg' },
        { label: 'Clubs', to: '/admin/clubs', icon: '/sidebar/club.svg' },
        { label: 'NGOs', to: '/admin/ngos', icon: '/sidebar/ngo.svg' },
        { label: 'Players', to: '/admin/players', icon: '/sidebar/club.svg' },
        { label: 'Settings', to: '/admin/settings', icon: '/sidebar/setting.svg' },
      ];
    case UserRole.CLUB:
      return [
        { label: 'Dashboard', to: '/club/dashboard', icon: '/sidebar/dashboard.svg' },
        { label: 'My Matches', to: '/club/matches', icon: '/sidebar/match.svg' },
        { label: 'Create Match', to: '/club/matches/create', icon: '/sidebar/create.svg' },
        { label: 'Browse NGO', to: '/club/browse-ngo', icon: '/sidebar/search.svg' },
        { label: 'Settings', to: '/club/profile', icon: '/sidebar/setting.svg' },
      ];
    case UserRole.NGO:
      return [
        { label: 'Dashboard', to: '/ngo/dashboard', icon: '/sidebar/dashboard.svg' },
        { label: 'Pledges', to: '/ngo/pledges', icon: '/sidebar/pledgesIcon.svg' },
        { label: 'Supporting Clubs', to: '/ngo/clubs', icon: '/sidebar/supportingClubsIcon.svg' },
        { label: 'Settings', to: '/ngo/profile', icon: '/sidebar/setting.svg' },
      ];
    case UserRole.PLAYER:
      return [
        { label: 'Dashboard', to: '/player/dashboard', icon: '/sidebar/dashboard.svg' },
        { label: 'My Matches', to: '/player/matches', icon: '/sidebar/match.svg' },
        { label: 'Create Matches', to: '/player/matches/create', icon: '/sidebar/create.svg' },
        { label: 'Browse NGOs', to: '/player/browse-ngo', icon: '/sidebar/ngo.svg' },
        { label: 'Settings', to: '/player/profile', icon: '/sidebar/setting.svg' },
      ];
    default:
      return [{ label: 'Public View', to: '/public', icon: '/globe.svg' }];
  }
}

function getRoleSubtitle(role: UserRole | undefined): string {
  switch (role) {
    case UserRole.ADMIN:
      return 'Manage your platform';
    case UserRole.CLUB:
      return 'Manage your club operations';
    case UserRole.NGO:
      return 'Manage your NGO operations';
    case UserRole.PLAYER:
      return 'Manage your player profile';
    default:
      return 'Welcome to Play For Goals';
  }
}

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const { user } = useAuth();
  const location = useLocation();
  const navItems = getNavItems(user?.role);
  const subtitle = getRoleSubtitle(user?.role);

  const firstName = (user?.name ?? user?.email?.split('@')[0] ?? 'User').split(' ')[0];
  const lastName = (user?.name ?? '').split(' ').slice(1).join(' ');

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-[272px] shrink-0 bg-white rounded-[35px] overflow-hidden">
        <SidebarContent
          navItems={navItems}
          subtitle={subtitle}
          firstName={firstName}
          lastName={lastName}
          location={location}
          onMobileClose={onMobileClose}
        />
      </aside>

      {/* Mobile sidebar — slides in as fixed overlay */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col w-[272px] bg-white rounded-r-2xl lg:hidden',
          'transition-transform duration-[250ms] ease-out',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <SidebarContent
          navItems={navItems}
          subtitle={subtitle}
          firstName={firstName}
          lastName={lastName}
          location={location}
          onMobileClose={onMobileClose}
        />
      </aside>
    </>
  );
}

interface SidebarContentProps {
  navItems: NavItem[];
  subtitle: string;
  firstName: string;
  lastName: string;
  location: ReturnType<typeof useLocation>;
  onMobileClose?: () => void;
}

function SidebarContent({
  navItems,
  subtitle,
  firstName,
  lastName,
  location,
  onMobileClose,
}: SidebarContentProps) {
  return (
    <div className="flex flex-col h-full px-8 py-7">
      {/* ── Logo row ── */}
      <div className="flex items-center gap-3 mb-16">
        {/* PFG badge */}
        <div className="relative w-11 h-11 shrink-0 flex items-center justify-center">
          <img src="/black logo.png" alt="logo" />
        </div>
        <span className="font-haas font-light text-[24px] text-[#1F1F1F] ">Play For Goals</span>
      </div>

      {/* ── Welcome block ── */}
      <div className="mb-6">
        <h2 className="font-haas font-medium text-[32px] leading-[1.08] text-[#1F1F1F]">
          Welcome
          <br />
          {firstName}
          {lastName ? ` ${lastName}` : ''}!
        </h2>
        <p className="font-haas font-light text-[14px] text-[#1F1F1F]/50 mt-2 ">{subtitle}</p>
      </div>

      {/* ── Divider ── */}
      <div className="h-px bg-[#1F1F1F]/[0.08] mb-6" />

      {/* ── Navigation ── */}
      <nav className="flex-1 overflow-y-auto">
        <p className="font-haas font-medium text-[16px] text-[#33333380] mb-3">Navigation</p>
        <div className="space-y-1">
          {navItems.map((item) => {
            // For the "Browse NGO" item, check for both "/player/browse-ngo" and "/player/ngo/*" to highlight it when on any NGO detail page
            const isActive =
              item.to === location.pathname ||
              (item.label === 'Browse NGO' &&
                // user?.role === UserRole.PLAYER &&
                location.pathname.startsWith('/player/ngo/'));

            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onMobileClose}
                className={cn(
                  'flex items-center gap-3 rounded-full px-4 py-3 text-[18px] font-normal font-haas transition-all duration-[200ms] ease-out',
                  isActive ? 'bg-[#0F6F4A] text-white' : 'text-[#111111] hover:bg-[#1F1F1F]/[0.04]'
                )}
              >
                <img
                  src={item.icon}
                  alt={item.label}
                  width={28}
                  height={28}
                  className={cn('shrink-0', isActive ? 'brightness-0 invert' : '')}
                />
                <span className="flex-1  truncate font-haas">{item.label}</span>
                {isActive && <ChevronRight size={15} className="shrink-0 opacity-60" />}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
